import { Component, RefObject, createRef } from 'react';
import Discover from '../../common/redux/actions/DiscoverActions';
import Dataset from '../../common/redux/actions/DatasetActions';
import { connect } from 'react-redux';
import { compose } from 'react-recompose';
import { Shelf } from '../shelf';
import _, { noop } from 'lodash';
import CalcDialog from '../calc/CalcDialog';
import { Viz } from '../VizUtil';
import FieldConfiguration from '../../datasets/FieldConfiguration';
import URLs from '../../common/Urls';
import { ROUTER_DIRS, Types } from '../../common/Constants';
import { VIZ_SELECTORS } from '../../common/redux/selectors/viz-selectors';
import { TextSearchField } from '../../components/ui/form';
import Util from '../../common/Util';
import { messages } from '../../i18n';
import { AddCircle } from '../../icons';
import { VizFieldSearchContainer } from './viz-layout-panel.styles';
import {
  addFieldDispatch,
  DeleteCalcPrompt,
  enforceAggregationType,
} from './viz-layout-panel.utils';
import {
  IVizLayoutPanelProps,
  IVizLayoutPanelState,
} from './viz-layout-panel.interfaces';
import { IChartShelf, IDiscovery } from '../interfaces';
import { FieldMenuActions } from '../../components/field-dropdown';
import {
  FieldMetadata,
  IAnyAttribute,
} from '../../datasets/interfaces/attribute.interface';
import { shortid } from '../../common/utilities/shortid-adapter';
import { useOpenVizDatasetSelector } from '../../common/redux/selectors/viz-selector.hook';
import { useDiscoverTheme } from '../../common/emotion';
import { useVizLayoutPanelContext } from './viz-layout-panel.context';
import { VizLayoutFields } from './viz-layout-fields';

class VizLayoutPanelUnconnected extends Component<
  IVizLayoutPanelProps,
  IVizLayoutPanelState
> {
  _scrollTimeout: any;
  dropdownCloseRef: RefObject<any>;

  constructor(props) {
    super(props);
    this.state = {
      showDeleteCalcPrompt: false,
      calcFieldToDelete: null,
      expandTimeFields: [],
      isScrolling: false,
    };
    this._scrollTimeout = null;
    this.onExpandToggleHandler = this.onExpandToggleHandler.bind(this);
    this.dropdownCloseRef = createRef();
  }

  componentDidUpdate(prevProps) {
    if (!_.isEqual(prevProps.expandTimeFields, this.props.expandTimeFields)) {
      this.setState({
        expandTimeFields: this.props.expandTimeFields,
      });
    }
  }

  onContextMenuAction(
    key,
    field,
    shelf = null,
    extraOpts: {
      fromShelf?: IChartShelf;
    } = {},
  ) {
    if (this.props.chartSpec?.shelves[key]) {
      // context key is a shelf Id
      const shelfId = key;
      const { fromShelf } = extraOpts;

      const from = Viz.findShelvesContainingField(
        this.props.viz.layout,
        field.name,
      );
      const fromWithoutSlicer = _.without(from, 'SLICER');
      const fieldOnlyOnSlicer = _.isEmpty(fromWithoutSlicer);
      const draggedFromSlicer = fromShelf?.id === 'SLICER';
      const draggedToSlicer = shelfId === 'SLICER';
      const movingInSameShelf =
        !_.isNil(fromShelf) && fromShelf?.id === shelf?.id;

      let moveToShelf = _.head(fromWithoutSlicer);

      if (
        (fieldOnlyOnSlicer ||
          (draggedFromSlicer && fieldOnlyOnSlicer) ||
          draggedToSlicer) &&
        !movingInSameShelf
      ) {
        // add it to the new shelf
        this.props.addField({
          discoveryId: this.props.viz.id,
          chartType: this.props.viz.chartType,
          field,
          shelf: shelfId,
          layout: this.props.viz.layout,
        });
      } else {
        if (movingInSameShelf) {
          moveToShelf = shelf.id;
        }
        this.props.moveFieldToAnotherShelf(field, moveToShelf, shelfId);
      }
    } else if (key === FieldMenuActions.REMOVE) {
      this.removeField(field, shelf?.id);
    } else if (key === FieldMenuActions.MOVE_DOWN) {
      this.props.moveFieldDown(field, shelf, this.props.viz.layout[shelf.id]);
    } else if (key === FieldMenuActions.MOVE_UP) {
      this.props.moveFieldUp(field, shelf, this.props.viz.layout[shelf.id]);
    } else if (key === FieldMenuActions.FILTER) {
      if (!_.isNil(field.parentField)) {
        this.props.showFieldFilterDialog(field.parentField);
      } else {
        this.props.showFieldFilterDialog(field);
      }
    } else if (key === FieldMenuActions.FILTER_AGGREGATE) {
      this.props.showFieldFilterAggregateDialog(field);
    } else if (key === FieldMenuActions.EDIT_CALC) {
      this.props.setShowFieldCalcDialog(true, field);
    } else if (key === FieldMenuActions.DELETE_CALC) {
      this.setState({
        showDeleteCalcPrompt: true,
        calcFieldToDelete: field,
      });
    } else if (key === FieldMenuActions.SORT_ASCENDING) {
      this.props.sortField({
        direction: 'asc',
        field,
        shelf,
        shelfFields: this.props.viz.layout[shelf.id],
      });
    } else if (key === FieldMenuActions.SORT_DESCENDING) {
      this.props.sortField({
        direction: 'desc',
        field,
        shelf,
        shelfFields: this.props.viz.layout[shelf.id],
      });
    } else if (key === FieldMenuActions.REMOVE_SORT) {
      this.props.sortField({
        direction: 'remove_sort',
        field,
        shelf,
        shelfFields: this.props.viz.layout[shelf.id],
      });
    } else if (key === FieldMenuActions.DESIGNATE_RUNTIME) {
      this.props.designateRuntimeFilter(field);
    } else if (key === FieldMenuActions.INCREASE) {
      this.props.updateFieldInViz(field, {
        [FieldMetadata.IS_DECREASE]: false,
      });
    } else if (key === FieldMenuActions.DECREASE) {
      this.props.updateFieldInViz(field, {
        [FieldMetadata.IS_DECREASE]: true,
      });
    }
  }
  removeField(field, shelfId) {
    if (this.props?.viz?.layout[shelfId]) {
      this.props.removeField(field, shelfId, this.props.viz.layout);
    }
  }

  onExpandToggleHandler(fieldName) {
    const { expandTimeFields } = this.state;
    this.setState({
      expandTimeFields: _.xor(expandTimeFields, [fieldName]),
    });
  }

  onDeleteCalcCancel() {
    this.setState({ showDeleteCalcPrompt: false });
  }

  onDeleteCalc() {
    this.props.deleteFieldCalc([this.state.calcFieldToDelete]);
    this.setState({ showDeleteCalcPrompt: false, calcFieldToDelete: null });
  }

  refresh() {
    if (Viz.isNewViz(this.props.viz.id)) {
      // this is a new, unsaved report. The viz.id won't be set but the discovery will have the generated id
      URLs.goTo(ROUTER_DIRS.OPEN_VIZ(this.props.viz.id));
    } else {
      URLs.goTo(ROUTER_DIRS.OPEN_VIZ(this.props.viz.id));
    }
  }

  hideDropdowns() {
    this.dropdownCloseRef.current.click();
  }

  handleScroll() {
    const { isScrolling } = this.state;
    if (!_.isEmpty(this._scrollTimeout)) {
      clearTimeout(this._scrollTimeout);
    }

    if (isScrolling) {
      clearTimeout(this._scrollTimeout);
      this._scrollTimeout = setTimeout(() => {
        this._scrollTimeout = null;
        this.setState({
          isScrolling: false,
        });
      }, 300);
    }
    if (!isScrolling) {
      this.hideDropdowns();
      this.setState({
        isScrolling: true,
      });
    }
  }

  render() {
    const { dataset } = this.props.viz;

    const shelves = Object.values(this.props.chartSpec?.shelves || {}).map(
      shelf => {
        return (
          <Shelf
            key={shelf.id}
            shelf={shelf}
            discoveryId={this.props.viz.id}
            missingFields={this.props.discovery.missingFields}
            onContextMenuAction={(key, f, fromShelf) =>
              this.onContextMenuAction(key, f, shelf, {
                fromShelf,
              })
            }
          />
        );
      },
    );

    return (
      <div className={'viz-layout'}>
        <div className={'layout-container'}>
          <div ref={this.dropdownCloseRef} className={'fields'}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
              }}
            >
              <label
                style={{
                  marginRight: '5px',
                }}
              >
                {messages.layoutPanel.fieldsLabel}
              </label>
              <AddCircle
                hover
                title={messages.layoutPanel.addCalcButtonTooltip}
                onClick={() => this.props.setShowFieldCalcDialog(true)}
              />
            </div>

            <VizFieldSearchContainer className='search-input'>
              <TextSearchField
                width='100%'
                value={this.props.searchFieldsValue}
                placeholder={messages.layoutPanel.searchPlaceholder}
                onChange={e => this.props.onFieldSearch(e)}
              />
              <hr />
            </VizFieldSearchContainer>

            <VizLayoutFields
              onContextMenuAction={(key, field) =>
                this.onContextMenuAction(key, field)
              }
              expandTimeFields={this.state.expandTimeFields}
              onExpandToggleHandler={fieldName =>
                this.onExpandToggleHandler(fieldName)
              }
              isScrolling={this.state.isScrolling ?? false}
              handleScroll={() => this.handleScroll()}
            />
          </div>
          <div className={'shelves'}>{shelves}</div>
        </div>

        <div
          style={{ marginBottom: '5px', maxWidth: '100%' }}
          className={'dataset'}
        >
          <FieldSettingsLink
            discoveryId={this.props.viz?.id}
            onClick={() =>
              this.props.setShowDatasetEditDialog(
                this.props.viz.dataset.id,
                true,
              )
            }
          />
        </div>
        <CalcDialog
          saveCalc={this.props.saveVizCalc}
          allFields={Viz.getAllFieldsInPlay(this.props.viz.layout)}
          viz={this.props.viz}
          fields={this.props.fields}
          datasetId={dataset?.id}
          currencySymbol={Viz.getCurrencySymbol(this.props.viz)}
        />
        <DeleteCalcPrompt
          show={this.state.showDeleteCalcPrompt}
          onCancel={() => this.onDeleteCalcCancel()}
          onConfirm={() => {
            this.onDeleteCalc();
          }}
          calcFieldToDelete={this.state.calcFieldToDelete}
          calcFields={this.props.calcFields}
        />
        {Util.isDatasetEditable(dataset) && (
          <FieldConfiguration
            discovery={this.props.discovery}
            onClose={() => {
              this.refresh();
            }}
          />
        )}
      </div>
    );
  }
}

const FieldSettingsLink = ({ discoveryId, onClick = noop }) => {
  const dataset = useOpenVizDatasetSelector({ discoveryId });

  const { colors: { LinkButtonTextColor } = {} } = useDiscoverTheme();

  if (!Util.isDatasetEditable(dataset)) {
    return null;
  }

  return (
    <span
      style={{
        color: LinkButtonTextColor,
        cursor: 'pointer',
      }}
      onClick={onClick}
    >
      {messages.formatString(messages.layoutPanel.configureFields, {
        name: dataset?.name,
      })}
    </span>
  );
};

const mapStateToProps = (state, ownProps) => {
  const discovery: IDiscovery = VIZ_SELECTORS.getActive(state, ownProps);

  const useFiscalCalendar =
    VIZ_SELECTORS.hasVizDatasetFiscalCalendarSetting(state, ownProps) &&
    VIZ_SELECTORS.getActiveVizFiscalSetting(state, ownProps) === 'true';
  const expandTimeFields = _.get(
    discovery,
    `expandTimeFields`,
    [] as IAnyAttribute[],
  );
  return {
    discovery,
    chartSpec: VIZ_SELECTORS.getActiveVizChartSpec(state, ownProps),
    useFiscalCalendar,
    expandTimeFields,
  };
};
const mapDispatchToProps = (dispatch, ownProps) => {
  return {
    addField: addFieldDispatch(dispatch),
    removeField: (field, shelf, layout) => {
      const priorPeriodRefs = Viz.fieldIsReferencedByPriorPeriodCalc(
        layout,
        field,
      );
      if (
        field.attributeType === Types.PRIOR_PERIOD_CALC ||
        !_.isEmpty(priorPeriodRefs)
      ) {
        // need to prompt the user first
        dispatch(
          Discover.showConfirmRemoveField(ownProps.discoveryId, field, shelf),
        );
      } else {
        dispatch(
          Discover.removeFieldFromVisualization(
            ownProps.discoveryId,
            field,
            shelf,
          ),
        );
      }
    },
    moveFieldToAnotherShelf: (field, fromShelf, toShelf) => {
      const movingField = enforceAggregationType({
        dispatch,
        discoveryId: ownProps.discoveryId,
        chartType: ownProps.discovery?.viz?.chartType,
        field,
        shelfId: toShelf,
      });

      dispatch(
        Discover.moveFieldToAnotherShelf(
          ownProps.discoveryId,
          movingField,
          fromShelf,
          toShelf,
        ),
      );
    },
    moveFieldUp: (field, shelf, shelfFields) => {
      const index = shelfFields.indexOf(field);
      const newShelfFields = _.slice(shelfFields, 0, index - 1)
        .concat([field, shelfFields[index - 1]])
        .concat(_.slice(shelfFields, index + 1));
      dispatch(
        Discover.reorderVizShelf(
          ownProps.discoveryId,
          shelf.id,
          newShelfFields,
        ),
      );
    },
    moveFieldDown: (field, shelf, shelfFields) => {
      const index = shelfFields.indexOf(field);
      const newShelfFields = _.slice(shelfFields, 0, index)
        .concat([shelfFields[index + 1], field])
        .concat(_.slice(shelfFields, Math.min(shelfFields.length, index + 2)));
      dispatch(
        Discover.reorderVizShelf(
          ownProps.discoveryId,
          shelf.id,
          newShelfFields,
        ),
      );
    },
    sortField: ({ field, shelf, shelfFields, direction }) => {
      dispatch(
        Discover.sortVizData({
          field,
          shelf,
          shelfFields,
          direction,
          discoveryId: ownProps.discoveryId,
        }),
      );
    },
    designateRuntimeFilter: field => {
      dispatch(
        Discover.designateRuntimeFilter({
          discoveryId: ownProps.discoveryId,
          field,
        }),
      );
    },
    updateFieldInViz: (field, fieldMeta = {}) => {
      const txId = shortid.generate();

      dispatch(
        Discover.updateFieldInViz(ownProps.discoveryId, field, fieldMeta, txId),
      );
    },
    showFieldFilterDialog: field => {
      dispatch(Discover.showFieldFilterDialog(field));
    },
    showFieldFilterAggregateDialog: field => {
      dispatch(Discover.showFieldFilterAggregateDialog(field));
    },
    setShowFieldCalcDialog: (show, field) => {
      dispatch(Discover.setShowFieldCalcDialog(show, field));
    },
    deleteFieldCalc: fields => {
      dispatch(Discover.deleteCalcFields(ownProps.discoveryId, fields));
    },
    setShowDatasetEditDialog: (datasetId, editable) => {
      dispatch(Dataset.editDataset(datasetId, editable));
    },
    showTimeHierarchy: (timeHierarchyFields, field) => {
      dispatch(
        Discover.showTimeHierarchy(
          ownProps.discoveryId,
          field,
          timeHierarchyFields,
        ),
      );
    },
    saveVizCalc: ({
      name,
      displayName,
      description,
      formula,
      dataType = Types.NUMBER,
      aggregation = 'Sum',
      aggregateMeasure = false,
      formatType = '',
      customFormatProps,
      hidden = false,
    }) => {
      const attributeType =
        dataType === Types.STRING
          ? Types.STRING_CALC
          : dataType === Types.TIMESTAMP
            ? Types.TIMESTAMP
            : dataType === Types.BOOLEAN
              ? Types.BOOLEAN
              : Types.CALC;

      // field is of type IAnyAttribute
      const field = {
        name,
        displayName: displayName ?? name,
        description,
        formula,
        attributeType,
        calcType: dataType,
        defaultAggregation: aggregateMeasure ? 'NONE' : aggregation,
        aggregateMeasure,
        formatType,
        customFormatProps,
        hidden,
      };

      const txId = shortid.generate();

      dispatch(Discover.saveCalcField(ownProps.discoveryId, field, txId));
      dispatch(
        Discover.updateFieldInViz(ownProps.discoveryId, field, undefined, txId),
      );
      dispatch(Discover.setShowFieldCalcDialog(false));
    },
  };
};

const VizLayoutPanelConnected = compose<any, any>(
  connect(mapStateToProps, mapDispatchToProps),
)(VizLayoutPanelUnconnected);

export const VizLayoutPanel = (props: any) => {
  const {
    viz,
    searchFieldsValue,
    hideFields,
    onFieldSearch,
    datasetAttributes,
    calcFields,
    timeHierarchies,
    fields,
    visibleFields,
    invisibleFields,
  } = useVizLayoutPanelContext();

  return (
    <VizLayoutPanelConnected
      {...props}
      viz={viz}
      discoveryId={viz?.id}
      searchFieldsValue={searchFieldsValue}
      hideFields={hideFields}
      onFieldSearch={onFieldSearch}
      datasetAttributes={datasetAttributes}
      calcFields={calcFields}
      timeHierarchies={timeHierarchies}
      fields={fields}
      visibleFields={visibleFields}
      invisibleFields={invisibleFields}
    />
  );
};
